<template>
  <div class="tw-w-full tw-h-full tw-relative tw-flex">
    <div class="embla tw-m-auto">
      <div
        class="tw-w-[90%] tw-bg-[#f3f3f3] tw-rounded-lg tw-absolute tw-h-[36px] tw-transition-all"
        style="pointer-events: none;"
        :class="{ 'tw-opacity-0': isSliding }"
      ></div>
      <div
        v-for="({ items, perspective, ref }, key) in list"
        :key="key"
        class="embla__ios-picker"
      >
        <div
          :ref="ref"
          class="embla__ios-picker__scene"
        >
          <div
            :class="[
              'embla__ios-picker__viewport',
              `embla__ios-picker__viewport--perspective-${perspective}`
            ]"
          >
            <div class="embla__ios-picker__container">
              <div
                v-for="(item, index) in items"
                :key="index"
                class="embla__ios-picker__slide"
              >
                {{ item.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmblaCarousel from 'embla-carousel'

const CIRCLE_DEGREES = 360
const WHEEL_ITEM_SIZE = 30
const WHEEL_ITEM_RADIUS = 360 / 18
const IN_VIEW_DEGREES = (360 / 18) * 4
const WHEEL_RADIUS = Math.round(30 / 2 / Math.tan(Math.PI / 18))

export default {
  name: 'WheelSlider',
  props: {
    list: {
      type: Array,
      required: true,
      default: () => ([
        {
          ref: 'emblaRef',
          perspective: 'left',
          items: [],
        },
      ]),
    },
  },
  emits: ['on-change'],
  data() {
    return {
      isSliding: false,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.list.forEach(item => {
        const emblaRef = this.$refs[item.ref][0]
        if (emblaRef) {
          this.setupIosPicker(emblaRef, {
            loop: true,
            axis: 'y',
          }, selectedIndex => {
            this.$emit('on-change', {
              ref: item.ref,
              selectedValue: item.items[selectedIndex],
            })
          }).scrollTo(item.selectedIndex || 0, true)
        }
      })
    })
  },
  methods: {
    setupIosPicker(iosPickerNode, options, onSelect) {
      const containerNode = iosPickerNode.querySelector('.embla__ios-picker__container')
      const emblaApi = EmblaCarousel(iosPickerNode, { ...options, container: containerNode })
      const slideCount = emblaApi.slideNodes().length
      const { loop } = emblaApi.internalEngine().options
      const totalRadius = slideCount * WHEEL_ITEM_RADIUS
      const rotationOffset = loop ? 0 : WHEEL_ITEM_RADIUS
      const rotateWheelFunc = this.rotateWheel(emblaApi, slideCount, rotationOffset)
      const rotateSlidesFunc = this.rotateSlides(emblaApi, loop, slideCount, totalRadius)
      const rotate = () => {
        rotateWheelFunc()
        rotateSlidesFunc()
        this.isSliding = true

        const velocity = Math.abs(emblaApi.internalEngine().scrollBody.velocity())
        if (velocity < 1) {
          this.isSliding = false
        }
      }

      emblaApi.on('pointerUp', () => {
        this.$nextTick(() => {
          const { scrollTo, target, location, scrollBody } = emblaApi.internalEngine()
          const diffToTarget = target.get() - location.get()
          let velocity = scrollBody.velocity()

          if (Math.abs(velocity) < 0.01) velocity = 0
          const velocityEffect = velocity * WHEEL_ITEM_SIZE * 0.5
          const dampingFactor = 20
          const adjustedDistance = (diffToTarget + velocityEffect) / dampingFactor

          scrollTo.distance(adjustedDistance, true)
        })
      })

      emblaApi.on('scroll', rotate)

      emblaApi.on('reInit', () => {
        this.inactivateEmblaTransform(emblaApi)
        rotate()
      })

      emblaApi.on('select', () => {
        const selectedIndex = emblaApi.selectedScrollSnap()
        onSelect(selectedIndex)
      })

      this.inactivateEmblaTransform(emblaApi)
      rotate()

      return emblaApi
    },
    rotateSlide(emblaApi, index, loop, slideCount, totalRadius) {
      const slideNode = emblaApi.slideNodes()[index]
      const wheelLocation = emblaApi.scrollProgress() * totalRadius
      const positionDefault = emblaApi.scrollSnapList()[index] * totalRadius
      const positionLoopStart = positionDefault + totalRadius
      const positionLoopEnd = positionDefault - totalRadius

      let inView = false
      let angle = index * -WHEEL_ITEM_RADIUS

      if (this.slideIsInView(wheelLocation, positionDefault)) {
        inView = true
      }

      if (loop && this.slideIsInView(wheelLocation, positionLoopEnd)) {
        inView = true
        angle = -CIRCLE_DEGREES + (slideCount - index) * WHEEL_ITEM_RADIUS
      }

      if (loop && this.slideIsInView(wheelLocation, positionLoopStart)) {
        inView = true
        angle = -(totalRadius % CIRCLE_DEGREES) - index * WHEEL_ITEM_RADIUS
      }

      if (inView) {
        slideNode.style.opacity = '1'
        slideNode.style.transform = `translateY(-${index * 100}%) rotateX(${angle}deg) translateZ(${WHEEL_RADIUS}px)`
      } else {
        slideNode.style.opacity = '0'
        slideNode.style.transform = 'none'
      }
    },
    rotateSlides(emblaApi, loop, slideCount, totalRadius) {
      return () => {
        for (let index = 0; index < slideCount; index += 1) {
          this.rotateSlide(emblaApi, index, loop, slideCount, totalRadius)
        }
      }
    },
    rotateWheel(emblaApi, slideCount, rotationOffset) {
      return () => {
        const rotation = slideCount * WHEEL_ITEM_RADIUS - rotationOffset
        const wheelRotation = rotation * emblaApi.scrollProgress()
        emblaApi.containerNode().style.transform = `translateZ(${WHEEL_RADIUS}px) rotateX(${wheelRotation}deg)`
      }
    },
    inactivateEmblaTransform(emblaApi) {
      const {
        translate, slideLooper,
      } = emblaApi.internalEngine()
      translate.clear()
      translate.toggleActive(false)
      slideLooper.loopPoints.forEach(({ translate }) => {
        translate.clear()
        translate.toggleActive(false)
      })
    },
    slideIsInView(wheelLocation, slidePosition) {
      return Math.abs(wheelLocation - slidePosition) < IN_VIEW_DEGREES
    },
  },
}
</script>

<style scoped>
.embla {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 16rem;
  max-width: 30rem;
  margin-left: auto;
  margin-right: auto;
}
.embla:before,
.embla:after {
  position: absolute;
  left: 0;
  right: 0;
  content: '';
  display: block;
  height: calc(50% - 32px / 2);
  z-index: 1;
  pointer-events: none;
}
.embla:before {
  top: -0.5px;
  border-bottom: 0;
  background: linear-gradient(
      to top,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 1) 100%
  );
}
.embla:after {
  bottom: -0.5px;
  border: 0;

  background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 1) 100%
  );
}
.embla__ios-picker {
  height: 100%;
  display: flex;
  align-items: center;
  min-width: 33%;
  justify-content: center;
  line-height: 1;
  font-size: 1.8rem;
}
.embla__ios-picker__scene {
  min-width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  touch-action: pan-y;
}
.embla__ios-picker__viewport {
  height: 32px;
  width: 100%;
  perspective: 1000px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.embla__ios-picker__viewport--perspective-left {
  perspective-origin: calc(50% + 130px) 50%;
  transform: translateX(27px);
}
.embla__ios-picker__viewport--perspective-center {
  perspective-origin: center;
}
.embla__ios-picker__viewport--perspective-right {
  perspective-origin: calc(50% - 130px) 50%;
  transform: translateX(-27px);
}
.embla__ios-picker__container {
  height: 100%;
  width: 100%;
  transform-style: preserve-3d;
  will-change: transform;
}
.embla__ios-picker__slide {
  width: 100%;
  height: 100%;
  font-size: 19px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  backface-visibility: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>